import { BasePointDragger } from "./BasePointDragger";
import {
  DraggerType,
  type Point3D,
  type TypedInputHint,
  type TypedInputMode,
} from "./types";
import { hasMinimumSeparation } from "./pointGeometry";
import {
  IDENTITY_TRANSFORM,
  uniformScaleTransform,
  type EntityTransform,
} from "../transform/entityTransform";

/**
 * Interactive "scale from base point" tool — AutoCAD SCALE with the Reference
 * option, which is the only variant that makes sense here.
 *
 * Three points: the base point (centre of scaling), a reference point whose
 * distance from the base defines the current length, and a third point whose
 * distance defines what that length should become. The factor is the ratio of
 * the two, so it works on a drawing at any unit scale — unlike AutoCAD's bare
 * default, where the raw distance from the base *is* the factor and is only
 * usable on a near-unit-scale drawing.
 *
 * The keypad short-circuits either half of that: once the base point is down a
 * typed factor scales and ends the command outright, never asking for the two
 * distances; once the reference point is down a typed length replaces the third
 * pick.
 */
export class ScaleDragger extends BasePointDragger {
  readonly type = DraggerType.Scale;

  protected readonly followUpPointCount = 2;

  /** Mode id of the bare-factor step, the one no point can express. */
  private static readonly FACTOR_MODE_ID = "factor";

  // One mode per step, so neither shows a mode selector: a ratio and a distance
  // along an already-fixed direction are both single numbers, and there is no
  // second way to express either.
  private static readonly FACTOR_INPUT_MODES: TypedInputMode[] = [
    {
      id: ScaleDragger.FACTOR_MODE_ID,
      fields: [{ key: "factor", label: "×", allowNegative: false }],
    },
  ];

  private static readonly LENGTH_INPUT_MODES: TypedInputMode[] = [
    {
      id: "newLength",
      fields: [{ key: "newLength", label: "L", allowNegative: false }],
    },
  ];

  /** Distance from the base point to the fixed reference point, if any. */
  private get referenceDistance(): number | null {
    const base = this.basePoint;
    const reference = this.followUpPoints[0];
    if (!base || !reference) return null;
    return Math.hypot(reference.x - base.x, reference.y - base.y);
  }

  /**
   * Refuse a reference point that sits on top of the base point: it would make
   * the reference length zero and the factor infinite. Uses the same pixel
   * heuristic the two-point shape draggers use to reject accidental clicks,
   * so the threshold stays meaningful at any zoom level.
   */
  protected override acceptFollowUpPoint(world: Point3D): boolean {
    const base = this.basePoint;
    if (!base) return false;
    return hasMinimumSeparation(
      this.viewer.worldToScreen(base),
      this.viewer.worldToScreen(world),
    );
  }

  protected computeTransform(current: Point3D): EntityTransform {
    const base = this.basePoint;
    const d1 = this.referenceDistance;
    // No reference length yet: nothing to preview, ghosts stay unscaled while
    // the rubber band tracks the cursor.
    if (!base || d1 === null || d1 === 0) return { ...IDENTITY_TRANSFORM };

    const d2 = Math.hypot(current.x - base.x, current.y - base.y);
    const factor = d2 / d1;
    // A zero or non-finite factor would collapse the geometry irrecoverably.
    if (!Number.isFinite(factor) || factor <= 0)
      return { ...IDENTITY_TRANSFORM };

    return uniformScaleTransform(factor, base);
  }

  protected commit(transform: EntityTransform): void {
    this.commitTransformInPlace(transform);
  }

  protected promptKey(): string {
    if (!this.basePoint) return "canvas:selection.basePointPrompt.base";
    return this.followUpPoints.length === 0
      ? "canvas:selection.basePointPrompt.reference"
      : "canvas:selection.basePointPrompt.newLength";
  }

  // ============== Typed input ==============

  /** Waiting for the reference point — the step where a bare factor is offered. */
  private get isFactorStep(): boolean {
    return !!this.basePoint && this.followUpPoints.length === 0;
  }

  protected override typedInputModes(): TypedInputMode[] | null {
    if (!this.basePoint) return null;
    return this.isFactorStep
      ? ScaleDragger.FACTOR_INPUT_MODES
      : ScaleDragger.LENGTH_INPUT_MODES;
  }

  protected override typedInputTitleKey(): string {
    return this.isFactorStep
      ? "canvas:tools.options.keypad.title.scaleFactor"
      : "canvas:tools.options.keypad.title.scaleLength";
  }

  /**
   * The measured reference length, shown but never edited. Without it the new
   * length is typed blind — you cannot tell whether 1000 doubles the selection
   * or halves it until you know what it is being divided by.
   */
  protected override typedInputHint(): TypedInputHint | null {
    const d1 = this.referenceDistance;
    if (this.isFactorStep || !d1) return null;
    return {
      labelKey: "canvas:tools.options.keypad.hint.referenceLength",
      value: d1,
    };
  }

  protected override typedInputValues(
    current: Point3D | null
  ): Record<string, number> {
    // Nothing to mirror at the factor step: the pointer is still choosing a
    // reference length, which says nothing about the factor. Start at 1.
    if (this.isFactorStep) return { factor: 1 };

    const base = this.basePoint;
    if (!base || !current) return { newLength: 0 };
    return { newLength: Math.hypot(current.x - base.x, current.y - base.y) };
  }

  /**
   * The third point a typed length stands for: at that distance from the base,
   * along the base -> reference direction. computeTransform() then reads the
   * factor off it as newLength / referenceDistance, unchanged.
   */
  protected override worldPointFromTypedValues(
    _modeId: string,
    values: Record<string, number>
  ): Point3D | null {
    const base = this.basePoint;
    const reference = this.followUpPoints[0];
    const d1 = this.referenceDistance;
    const newLength = values.newLength;
    if (!base || !reference || !d1) return null;
    if (!Number.isFinite(newLength) || newLength <= 0) return null;

    return {
      x: base.x + ((reference.x - base.x) / d1) * newLength,
      y: base.y + ((reference.y - base.y) / d1) * newLength,
      z: base.z,
    };
  }

  // The factor step is the one thing here that no point can express — there is
  // no reference distance yet to divide by — so it bypasses the point path
  // entirely and drives the transform directly. Every other step falls through
  // to worldPointFromTypedValues() above.

  override previewTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const transform = this.typedFactorTransform(modeId, values);
    if (!transform) {
      super.previewTypedInput(modeId, values);
      return;
    }
    this.previewTransform(transform);
  }

  override commitTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const transform = this.typedFactorTransform(modeId, values);
    if (!transform) {
      super.commitTypedInput(modeId, values);
      return;
    }
    this.commit(transform);
    this.viewer.update();
    this.finish();
  }

  /** The transform a typed factor implies, or null outside the factor step. */
  private typedFactorTransform(
    modeId: string,
    values: Record<string, number>
  ): EntityTransform | null {
    const base = this.basePoint;
    const factor = values.factor;
    if (modeId !== ScaleDragger.FACTOR_MODE_ID || !base) return null;
    if (!Number.isFinite(factor) || factor <= 0) return null;
    return uniformScaleTransform(factor, base);
  }
}
